import { useEffect, useState } from "react";
import Form from "../../../Components/Form";
import FileInput from "../../../Components/FileInput/FileInput";
import DownloadOrderTemplate from "../../../Components/DownloadOrderTemplate";
import Button from "../../../Components/Button";
import useRequest from "../../../Hooks/useRequest";
import { toast } from "react-toastify";
import { CommonTableUI } from "../../../Tables/TableTemplate/common";
import { ErrorTableReport } from "../../../Interfaces/orderInterfaces";
import SahraConnections from "../../Sahra/SahraConnections";
import Modal from "../../../Components/Modal/Modal";
import { Report } from "../../../Interfaces/TableInterfaces";
import FileBulkOrderTableUI from "../../../Tables/Order/FileBulkOrder/FileBulkOrderTableUI";
import Loading from "../../../Components/Loading/Loading";
import {
  addNLegsBulkOrder,
  uploadNLegsOrderFile,
} from "../../../Requests/nLegsRequests";
import {
  IBulkOrderRow,
  INLegFileUploadResp,
} from "../../../Interfaces/nLegsInterfaces";
import ExcelNLegOrderPreviewTable from "../../../Tables/NLegs/ExcelNLegOrderPreviewTable";

enum ModalMode {
  BulkOrderPreview,
  BulkOrderUsers,
  BulkOrderResponseTable,
}

interface IProps {
  legCount: 2 | 3 | 4;
}

const BulkOrder = ({ legCount }: IProps) => {
  const [excelFile, setExcelFile] = useState<FormData | null>(null);
  const [bulkOrderRows, setBulkOrderRows] =
    useState<Array<IBulkOrderRow> | null>(null);
  const [uploadFileErrorTableData, setUploadFileErrorTableData] =
    useState<Report<ErrorTableReport> | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState(ModalMode.BulkOrderPreview);

  function errorHandler(e: any) {
    if (e.response.status === 400) {
      setUploadFileErrorTableData(e.response.data);
      setIsModalOpen(true);
      setModalMode(ModalMode.BulkOrderPreview);
    }

    if (e.response.status === 500) toast.error("خطا داخلی");
  }

  const addBulkOrderRequest = useRequest(
    () => addNLegsBulkOrder(bulkOrderRows),
    (e) => e.response.data.value
  );

  const fileOrderRequest = useRequest(
    () => uploadNLegsOrderFile(excelFile, legCount),
    errorHandler
  );

  const bulkOrderClickHandler = () => {
    if (fileOrderRequest.loading) return;
    fileOrderRequest.fetchData();
  };

  const getOrderRows = (reports: Array<INLegFileUploadResp>) => {
    setBulkOrderRows(reports.map((report) => report.orderRow));
  };

  useEffect(() => {
    if (fileOrderRequest.response) {
      setIsModalOpen(true);
      setModalMode(ModalMode.BulkOrderPreview);
      getOrderRows(fileOrderRequest.response.report);
      setUploadFileErrorTableData(null);
    }
  }, [fileOrderRequest.response]);

  useEffect(() => {
    if (!addBulkOrderRequest.response) return;

    if (addBulkOrderRequest.response.report?.length) {
      setModalMode(ModalMode.BulkOrderResponseTable);
      return;
    }

    toast.success("سفارش‌های مجاز با موفقیت ثبت شدند");
    setIsModalOpen(false);
    setExcelFile(null);
    setBulkOrderRows(null);
    addBulkOrderRequest.resetResponse();
  }, [addBulkOrderRequest.response]);

  const getBulkOrderPreviewTable = () => {
    if (uploadFileErrorTableData)
      return (
        <div className="flex flex-col justify-between items-center h-full">
          <div className="border-2 border-white border-b-red-700 text-red-700 w-full text-center mb-3">
            خطا
          </div>
          <CommonTableUI reports={uploadFileErrorTableData} />
        </div>
      );
    if (fileOrderRequest.response)
      return (
        <div className="flex flex-col justify-between items-center h-full">
          <ExcelNLegOrderPreviewTable reports={fileOrderRequest.response} />
          <div className="mt-3 w-full">
            <Button
              onClick={() => setModalMode(ModalMode.BulkOrderUsers)}
              text="ارسال"
              variant="contained"
            />
          </div>
        </div>
      );
    else return <></>;
  };

  const getBulkOrderSahraConnectionsModalUI = () => {
    return (
      <div className="flex flex-col justify-start items-center gap-3">
        <SahraConnections initialSearchParam={{ userIds: getUserIds() }} />
        <Button
          onClick={bulkOrderSahraConnectionsModalBtnClickHandler}
          text="ارسال"
          variant="contained"
          loading={addBulkOrderRequest.loading}
        />
      </div>
    );
  };

  const getFileBulkOrderTableUI = () => {
    if (addBulkOrderRequest.loading)
      return (
        <div className="flex justify-center items-center w-full h-60">
          <Loading className="!text-sky-500" />
        </div>
      );
    if (addBulkOrderRequest.response)
      return <FileBulkOrderTableUI reports={addBulkOrderRequest.response} />;
  };

  const getModalUI = () => {
    switch (modalMode) {
      case ModalMode.BulkOrderPreview:
        return getBulkOrderPreviewTable();

      case ModalMode.BulkOrderUsers:
        return getBulkOrderSahraConnectionsModalUI();

      case ModalMode.BulkOrderResponseTable:
        return getFileBulkOrderTableUI();
    }
  };

  const getUserIds = () => {
    if (!bulkOrderRows) return [];
    const res: number[] = [];

    bulkOrderRows.forEach((row) => {
      res.push(row.userId);
    });
    return res;
  };

  const bulkOrderSahraConnectionsModalBtnClickHandler = () => {
    addBulkOrderRequest.resetResponse();
    addBulkOrderRequest.fetchData();
    setModalMode(ModalMode.BulkOrderResponseTable);
  };

  const modalCloseHandler = () => {
    switch (modalMode) {
      case ModalMode.BulkOrderPreview: {
        setIsModalOpen(false);
        break;
      }
      case ModalMode.BulkOrderUsers: {
        setModalMode(ModalMode.BulkOrderPreview);
        break;
      }
      case ModalMode.BulkOrderResponseTable: {
        setIsModalOpen(false);
        break;
      }

      default:
        break;
    }
  };

  return (
    <Form>
      {isModalOpen ? (
        <Modal onClose={modalCloseHandler} UI={getModalUI()} />
      ) : (
        <></>
      )}
      <div className="p-2 w-full">
        <div className="bold mb-2 text-xs">ارسال دسته ای</div>
        <div className="flex flex-wrap items-center gap-3">
          <FileInput
            fileExtension=".xlsx, .xls, .csv"
            UI={<></>}
            onChange={setExcelFile}
            value={excelFile}
            name="formFile"
            label="انتخاب فایل اکسل"
          />
          <DownloadOrderTemplate kind={`nleg-${legCount}`} />
        </div>
        <div className="mt-3 w-full">
          <Button
            text="ارسال"
            onClick={bulkOrderClickHandler}
            variant="contained"
            disabled={!excelFile || fileOrderRequest.loading}
            loading={fileOrderRequest.loading}
          />
        </div>
      </div>
    </Form>
  );
};

export default BulkOrder;
